// Atlas-weite Schriftart. Muster: ui/fontScale.tsx (Wert-Tabelle + Provider +
// localStorage) plus die Server-Rehydrierung aus i18n/index.tsx.
//
// Warum die Rehydrierung HIER liegt und nicht (wie bei font_scale) in
// Settings.tsx: die Schriftart ist eine NUTZER-Einstellung und gilt auf jedem
// Gerät. Läge das Nachladen auf der Einstellungs-Seite, sähe ein Mitarbeiter an
// einem zweiten Rechner so lange Inter, bis er zufällig die Einstellungen
// aufruft — die Wahl wäre gespeichert und trotzdem unsichtbar.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { api } from "../api";

export type UiFont =
  | "system"
  | "inter"
  | "open-sans"
  | "aptos"
  | "ibm-plex"
  | "source-sans"
  | "atkinson";

/** Der heutige Stack aus tailwind.config.js — unverändert. Er ist der Default
 *  von --ui-font und muss es bleiben: wer nie etwas wählt, bekommt exakt das
 *  Bild von vorher, bis auf das letzte Pixel. */
const INTER_STACK =
  '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

/** Schwanz für die gebündelten Web-Fonts: lädt die woff2 nicht (oder blockiert
 *  ein Proxy sie), fällt die Oberfläche auf Inter zurück und nicht auf Times. */
const INTER_FALLBACK =
  '"Inter Variable", Inter, -apple-system, "Segoe UI", system-ui, sans-serif';

/** Windows-Systemschrift. „Segoe UI Variable Text" ist die Text-Optik von
 *  Win11; auf Win10/macOS/Linux greift der Rest der Liste. */
const SEGOE_STACK = '"Segoe UI Variable Text", "Segoe UI", system-ui, sans-serif';

export const UI_FONT_STACKS: Record<UiFont, string> = {
  inter: INTER_STACK,
  system: SEGOE_STACK,
  "open-sans": `"Open Sans Variable", "Open Sans", ${INTER_FALLBACK}`,
  // Aptos wird NICHT gebündelt: Microsofts Lizenz erlaubt kein Self-Hosting.
  // Der NAME im Stack ist lizenzkonform (er beschreibt nur, was der Rechner
  // hergibt) und greift genau dort, wo die IT Aptos ausgerollt hat. Sonst
  // rendert Segoe UI — deshalb steht am Schalter ein ehrlicher Hinweis
  // (`fontInstalliert`), statt dem Nutzer eine Wahl ohne Wirkung zu zeigen.
  aptos: `Aptos, "Aptos Display", ${SEGOE_STACK}`,
  "ibm-plex": `"IBM Plex Sans Variable", "IBM Plex Sans", ${INTER_FALLBACK}`,
  "source-sans": `"Source Sans 3 Variable", "Source Sans 3", ${INTER_FALLBACK}`,
  atkinson: `"Atkinson Hyperlegible Next Variable", "Atkinson Hyperlegible Next", ${INTER_FALLBACK}`,
};

/** Werte in Anzeige-Reihenfolge. Beschriftung + Hinweis kommen aus dem Katalog
 *  (`settings.schriftart[wert]`) — eine Tabelle kennt keine Sprache (Muster
 *  ui/fontScale.tsx, ui/labels.ts). `systemName` trägt den Namen der Schrift,
 *  die auf DIESEM Gerät geprüft werden muss (nur bei Aptos: sie ist nicht
 *  gebündelt). */
export const UI_FONT_OPTIONS: { value: UiFont; systemName?: string }[] = [
  { value: "inter" },
  { value: "system" },
  { value: "open-sans" },
  { value: "aptos", systemName: "Aptos" },
  { value: "ibm-plex" },
  { value: "source-sans" },
  { value: "atkinson" },
];

const STORAGE_KEY = "sb_ui_font";
export const DEFAULT_UI_FONT: UiFont = "inter";

/** x-Höhe von Inter, gemessen als Anteil der em-Größe. Die sieben Optionen
 *  streuen zwischen 0,476 und 0,546 (~15 %) — dieselbe Punktgröße sähe je nach
 *  Schrift verschieden gross aus, und der Nutzer läse das als „die Einstellung
 *  hat auch die Grösse verstellt".
 *
 *  `font-size-adjust: ex-height 0.546` normiert die GLYPHEN auf Inters x-Höhe,
 *  ohne die berechnete `font-size` anzufassen — das rem-Raster (Abstände,
 *  Zeilenhöhen, --font-scale aus index.css) bleibt damit unberührt. Bei `inter`
 *  wird die Eigenschaft ENTFERNT statt auf den Anker gesetzt: der Default-Nutzer
 *  soll nicht einmal durch einen No-Op-Umweg laufen.
 *
 *  Verifiziert in Chromium/Edge. UNBEKANNT für Safari: die Zwei-Wert-Syntax
 *  (`ex-height <n>`) ist dort jünger als die Ein-Wert-Form; fällt sie als
 *  ungültige Deklaration weg, wirkt schlicht keine Normierung — die Schrift
 *  steht dann etwas kleiner oder grösser da, nichts bricht. */
export const X_HOEHEN_ANKER = "ex-height 0.546";

interface Ctx {
  font: UiFont;
  /** Schaltet sofort um und speichert am Nutzer. Wirft, wenn der PUT scheitert
   *  — die Oberfläche steht dann bereits auf der neuen Schrift, aber der
   *  Aufrufer soll sagen können, dass es nicht gespeichert wurde. */
  setFont: (f: UiFont) => Promise<void>;
  /** true, solange der PUT läuft (für ein „· speichere …" am Schalter). */
  speichert: boolean;
}

const UiFontCtx = createContext<Ctx | null>(null);

export function istUiFont(v: unknown): v is UiFont {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(UI_FONT_STACKS, v);
}

function getInitial(): UiFont {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return istUiFont(saved) ? saved : DEFAULT_UI_FONT;
}

function applyToRoot(font: UiFont) {
  const el = document.documentElement;
  el.style.setProperty("--ui-font", UI_FONT_STACKS[font]);
  if (font === DEFAULT_UI_FONT) {
    // Default = unangetastet: --font-numeric bleibt Lexend (themes.css) und die
    // x-Höhen-Normierung entfällt.
    el.style.removeProperty("font-size-adjust");
    el.style.removeProperty("--font-numeric");
    return;
  }
  el.style.setProperty("font-size-adjust", X_HOEHEN_ANKER);
  // Zahlen folgen der UI-Schrift, sobald der Nutzer eine gewählt hat: sonst
  // mischte sich Lexend in jede KPI-Karte einer ansonsten durchgängig anderen
  // Oberfläche. Nur im Default bleibt Lexend stehen — dort ist es die bewusste
  // v8-Entscheidung (themes.css:50).
  // Altbefund, hier NICHT behoben: Lexend hat keine `tnum`-Feature-Tabelle, das
  // `font-feature-settings: "tnum" 1` an .ea-num/KpiCard läuft dort also ins
  // Leere. Betrifft den Default und ist von dieser Einstellung unabhängig.
  el.style.setProperty("--font-numeric", UI_FONT_STACKS[font]);
}

const FANTASIE = "sb-diese-schrift-gibt-es-nicht-42";

/** Rendert dieses Gerät die Schrift `name` wirklich, oder fällt es still auf
 *  etwas anderes zurück? Gemessen statt behauptet: die Textbreite gegen einen
 *  Fantasie-Namen (der garantiert auf die Fallback-Schrift fällt). Zwei
 *  Fallback-Generics, damit ein zufällig gleich breites Paar nicht als „nicht
 *  installiert" durchgeht. */
export function fontInstalliert(name: string): boolean {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return false;
  const probe = "ẞMmWwIl0O Handgloves 123";
  const breite = (stack: string) => {
    ctx.font = `24px ${stack}`;
    return ctx.measureText(probe).width;
  };
  for (const generisch of ["monospace", "serif"]) {
    const basis = breite(`"${FANTASIE}", ${generisch}`);
    if (Math.abs(breite(`"${name}", ${generisch}`) - basis) > 0.5) return true;
  }
  return false;
}

export function UiFontProvider({ children }: { children: ReactNode }) {
  const [font, setFontState] = useState<UiFont>(getInitial);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    applyToRoot(font);
    localStorage.setItem(STORAGE_KEY, font);
  }, [font]);

  // Server-Rehydrierung. Fehler werden geschluckt: wer nicht angemeldet ist
  // (Login-Maske) oder gerade keine Settings lesen kann, bekommt den
  // localStorage-Stand — eine Präferenz darf den Start nicht blockieren
  // (dieselbe Begründung wie in i18n/index.tsx).
  useEffect(() => {
    let lebt = true;
    void api
      .get<{ ui?: { font_family?: unknown } }>("/settings")
      .then((res) => {
        const v = res?.ui?.font_family;
        if (lebt && istUiFont(v)) setFontState(v);
      })
      .catch(() => {
        /* siehe oben */
      });
    return () => {
      lebt = false;
    };
  }, []);

  const setFont = useCallback(async (naechste: UiFont) => {
    // Erst umschalten, dann speichern: die Umstellung ist sofort sichtbar und
    // hängt nicht am Netz.
    setFontState(naechste);
    setSpeichert(true);
    try {
      // Partieller PUT — weder die Modelle noch die Schriftgrösse noch die
      // Quellen-Voreinstellung werden mitgeschickt und dürfen deshalb auch
      // nicht überschrieben werden (der Router merged feldweise).
      await api.put("/settings", { ui: { font_family: naechste } });
    } finally {
      setSpeichert(false);
    }
  }, []);

  const wert = useMemo(() => ({ font, setFont, speichert }), [font, setFont, speichert]);
  return <UiFontCtx.Provider value={wert}>{children}</UiFontCtx.Provider>;
}

export function useUiFont(): Ctx {
  const v = useContext(UiFontCtx);
  if (!v) throw new Error("useUiFont outside UiFontProvider");
  return v;
}
